
import React, { memo, useContext, useEffect, useState } from 'react';
import { Col, Form, message, Row } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { ModalContext } from 'src/contexts/modal-context';
import { editableFields } from '../constants';
import { FormBuilder } from '@components/form';
import { translate } from '@components/i18n';
import { ItemResponse } from '@models/item';
import { createRestrictedItem, removeRestrictedItem, fetchRestrictedItems } from '@redux/slices/restricted-items';
import { divisionValues } from '@redux/slices/divisions';
import { orderTypeSelector } from '@redux/slices/order';
import { fetchDivisions } from '@redux/slices/divisions';
import { fetchAllItems } from '@redux/slices/campaign';
import { campaignSelector} from '@redux/slices/campaign';

interface EditModalProps {
  data?: ItemResponse;
}

export const EditModal: React.FC<EditModalProps> = memo(({ data }) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm<ItemResponse>();
    const [editState, setEditState] = useState<ItemResponse>(data);
    const divisions = useSelector(divisionValues);
    const { allAvailablePlatforms } = useSelector(orderTypeSelector);
    const modalContext = useContext(ModalContext);
    const campainState = useSelector(campaignSelector);

    const allItems = campainState.allItems
        .filter(item => item.divisao === form.getFieldValue("divisao"))
        .map(c => ({
            id: `${c.numero}-x${c.multiplo || 1}`,
            name: `${c.numero} - ${c.descricao} - x${c.multiplo || 1}`,
        })).removeDuplicatesById()

    useEffect(() => {
      dispatch(fetchDivisions());
      dispatch(fetchAllItems());
    }, [])

    const onOk = (values): void => {
      if (!data) addNew(values);

      modalContext.handleClose();
    };

    const addNew = (values): void => {

        const orderKey = `${new Date().valueOf()}`;
        message.loading({
            content: translate('general.registeringItem'),
            key: orderKey,
            duration: 10,
            className: 'message-styled',
        });

        dispatch(
            createRestrictedItem({
                data: {
                  divisao: values.divisao,
                  numeros: values.numeros.map(item => item.split("-")[0]),
                },
                notificationKey: orderKey,
                onSuccess: () => {
                    modalContext.handleClose();
                    dispatch(fetchRestrictedItems())
                },
            }),
        );
    };

    useEffect(() => {
        if (data) {
          const aux = {
            ...data,
            numeros: [`${data.numero}-x${data.multiplo || 1}`]

          }
          setEditState(aux);
          form.setFieldsValue(aux);
        } else {
          form.resetFields();
        }
    }, [data]);

    const onChangeValues = (
        changedValues: Partial<ItemResponse>,
        allValues: ItemResponse,
    ): void => {
        setEditState({
            ...allValues,
            ...changedValues,
        });
    };

    const handleDeleteUser = (): void => {
      const orderKey = `${new Date().valueOf()}`;
      dispatch(removeRestrictedItem({
        data: data.id,
        notificationKey:orderKey,
        onSuccess: () => {
          dispatch(fetchRestrictedItems())
        }
      }))
      modalContext.handleClose();
    };

    return (
        <Row align="middle">
            <Col span={1} />
            <Col span={22}>
                <FormBuilder
                    onValuesChange={onChangeValues}
                    layout="vertical"
                    labelCol={{ span: 4 }}
                    wrapperCol={{ span: 24 }}
                    initialValues={{ ...editState }}
                    onFinish={onOk}
                    form={form}
                    fields={editableFields({
                        withRow: true,
                        editable: !data,
                        optionsDivisions: divisions.map((item) => ({
                            id: item.nome,
                            name: item.nome + ' - ' + item.descricao,
                        })),
                        optionsItems: allItems
                    })}
                    withDelete={!!data}
                    onDelete={handleDeleteUser}
                />
            </Col>
            <Col span={1} />
        </Row>
    );
});
